import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ElectronService } from 'ngx-electron';
import { InputDevice, SettingsState } from '@app/shared/settings/settings.state';
import { SetDefaultDesktopInput, SetDefaultInput } from '@app/shared/settings/settings.actions';
import { Select, Store } from '@ngxs/store';


@Injectable({
  providedIn: 'root'
})
export class InputService {
  @Select(SettingsState.defaultInput) defaultInput: Observable<InputDevice>;
  @Select(SettingsState.defaultDesktopInput) defaultDesktopInput: Observable<InputDevice>;
  constructor(private electronService: ElectronService, private store: Store) {}

  public inputDevices(): Observable<InputDevice[]> {
    return this.inputSources().pipe(switchMap(res => {
      if(res?.[0]?.name === ''){
        return this.requestMicPermission().pipe(switchMap(stream => this.inputSources()))
      } else {
        return this.inputSources();
      }
    }));
  }

  public desktopInputDevices(): Observable<InputDevice[]>{
    return from(
      this.electronService.desktopCapturer.getSources({
        types: ['screen', 'window']
      })
    ).pipe(
      map(sources =>
        sources.map(source => ({ id: source.id, name: source.name, isDesktop: true }))
      )
    );
  }

  public inputDevicesAsSelectOptions(){
    return this.inputDevices().pipe(
      withLatestFrom(this.defaultInput),
      // @ts-ignore
      // tap(([devices]) => (window.devices = devices)),
      map(([devices, defaultDevice]) => {


        const selectOptions = devices.map(device => ({
          label: device.name,
          value: device
        }));

        const defaultOption = defaultDevice.id
          ? selectOptions.find(opt => opt.value.id === defaultDevice.id)
          : selectOptions[0];

        if (!defaultDevice.id) {
          this.store.dispatch(
            new SetDefaultInput(
              devices.find(device => device.id === defaultOption.value.id)
            )
          );
        }

        return { selectOptions, defaultDevice, selectedOption: defaultOption };
      })
    );
  }

  public desktopInputOptionsAsSelectOptions() {
    return this.desktopInputDevices().pipe(
      withLatestFrom(this.defaultDesktopInput),
      // @ts-ignore
      // tap(([devices]) => (window.devices = devices)),
      map(([devices, defaultDevice]) => {


        const selectOptions = devices.map(device => ({
          label: device.name,
          value: device
        }));


        return selectOptions;
      })
    );
  }

  requestMicPermission(){
    return this.requestAudioPermission().pipe(
      // @ts-ignore
      switchMap(stream =>
        this.inputSources().pipe(
          tap(() => {
            if (stream instanceof MediaStream) {
              stream.getTracks().forEach(function(track) {
                track.stop();
              });
            }
          }),
        )
      )
    );
  }

  private requestAudioPermission() {
    return this.requestAudioPermissionWeb();
  }

  private requestAudioPermissionWeb() {
    return from(
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    );
  }

  private inputSources(): Observable<InputDevice[]> {
      return this.inputSourcesWeb();
  }

  private inputSourcesWeb() {
    return from(navigator.mediaDevices.enumerateDevices()).pipe(
      map(devices => {

        return devices
          .filter(device => device.kind === 'audioinput')
          .map(device => ({ name: device.label, id: device.deviceId }));
      })
    );
  }



  private isElectronApp() {
    const isElectronApp = this.electronService.isElectronApp;

    return isElectronApp;
  }
}
